import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '@/components/sections/Header';
import Footer from '@/components/sections/Footer';
import { sendWaitlistEmail, type WaitlistFormData } from '@/services/emailService';

const WaitlistPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    organization: '',
    interest: '',
    farmSize: '',
    location: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Send email with form data
      const emailSent = await sendWaitlistEmail(formData as WaitlistFormData);
      
      if (emailSent) {
        setIsSubmitted(true);
        // Optional: Save to local storage for backup
        localStorage.setItem('waitlist_submission', JSON.stringify({
          ...formData,
          submittedAt: new Date().toISOString()
        }));
      } else {
        setSubmitError('Failed to send your application. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError('An unexpected error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const interestOptions = [
    { value: 'farmer', label: 'I am a farmer looking to sell directly' },
    { value: 'consumer', label: 'I want to buy fresh produce directly' },
    { value: 'investor', label: 'I am interested in investing' },
    { value: 'partner', label: 'I want to partner with FoodSply' },
    { value: 'developer', label: 'I am a developer interested in the platform' },
    { value: 'other', label: 'Other' }
  ];

  const roleOptions = [
    'Farmer',
    'Consumer',
    'Investor',
    'Distributor',
    'Retailer',
    'Cooperative Member',
    'AgTech Professional',
    'Blockchain Developer',
    'Other'
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-foodsply-green to-foodsply-orange flex items-center justify-center">
        <Header />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center text-white p-8"
        >
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
            <img src="/images/check.svg" alt="Success" className="w-10 h-10" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Welcome to the FoodSply Family!</h1>
          <p className="text-xl mb-8 max-w-2xl">
            Thank you for joining our waitlist. You'll be among the first to know when we launch 
            and get early access to revolutionize how we grow, trade, and consume food.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.href = '/'}
            className="bg-white text-foodsply-green px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors"
          >
            Back to Home
          </motion.button>
        </motion.div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-foodsply-green to-foodsply-orange text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              Join the Food Revolution
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Be among the first to experience the future of agriculture. 
              Connect directly with farmers, trade digital assets, and build a sustainable food system.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <img src="/images/users-group-white.svg" alt="" className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Early Access</h3>
                <p className="opacity-90">Get exclusive early access to our platform before public launch</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <img src="/images/gift-white.svg" alt="" className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Special Rewards</h3>
                <p className="opacity-90">Receive exclusive rewards and benefits as a founding member</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <img src="/images/lightbulb-white.svg" alt="" className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Shape the Future</h3>
                <p className="opacity-90">Influence our development with your feedback and ideas</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Waitlist Form */}
      <section className="py-20">
        <div className="container mx-auto px-4" ref={ref}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                Secure Your Spot
              </h2>
              <p className="text-xl text-gray-600">
                Tell us about yourself and how you'd like to be part of the FoodSply ecosystem
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-foodsply-green focus:border-transparent transition-all"
                    placeholder="Enter your first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-foodsply-green focus:border-transparent transition-all"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-foodsply-green focus:border-transparent transition-all"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-foodsply-green focus:border-transparent transition-all"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Role *
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-foodsply-green focus:border-transparent transition-all"
                  >
                    <option value="">Select your role</option>
                    {roleOptions.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-foodsply-green focus:border-transparent transition-all"
                    placeholder="Company, Farm, or Organization"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-foodsply-green focus:border-transparent transition-all"
                    placeholder="City, State/Province, Country"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Farm Size (if applicable)
                  </label>
                  <input
                    type="text"
                    name="farmSize"
                    value={formData.farmSize}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-foodsply-green focus:border-transparent transition-all"
                    placeholder="e.g., 50 acres, 200 hectares"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Primary Interest *
                </label>
                <select
                  name="interest"
                  value={formData.interest}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-foodsply-green focus:border-transparent transition-all"
                >
                  <option value="">Select your primary interest</option>
                  {interestOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Additional Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-foodsply-green focus:border-transparent transition-all resize-none"
                  placeholder="Tell us more about your interest in FoodSply, specific needs, or questions you have..."
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-foodsply-green to-foodsply-orange text-white py-4 px-8 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Joining Waitlist...
                  </>
                ) : (
                  <>
                    Join the Waitlist
                    <img src="/images/arrow-right-1-white.svg" alt="" className="w-5 h-5 ml-2" />
                  </>
                )}
              </motion.button>

              {submitError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-600 text-sm text-center">{submitError}</p>
                </div>
              )}

              <p className="text-center text-gray-500 text-sm mt-4">
                By joining our waitlist, you agree to receive updates about FoodSply. 
                We respect your privacy and will never share your information.
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WaitlistPage;